import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, requestYieldCallback } from '@theme/utilities';

const SEARCH_MODAL_ID = 'search-modal';
const SEARCH_OVERLAY_FROZEN_CLASS = 'search-overlay-frozen';
/** Matches `.dialog-modal.search-modal__content.dialog-closing` duration. */
const SEARCH_MODAL_CLOSE_MS = 200;

function getSearchOverlayFreezeTarget() {
  const main = document.getElementById('MainContent');
  return main instanceof HTMLElement ? main : null;
}

function freezePageBehindSearchModal() {
  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  getSearchOverlayFreezeTarget()?.classList.add(SEARCH_OVERLAY_FROZEN_CLASS);
}

function unfreezePageBehindSearchModal() {
  getSearchOverlayFreezeTarget()?.classList.remove(SEARCH_OVERLAY_FROZEN_CLASS);
}

/** @type {Set<any>} */
const swipersToResume = new Set();
/** @type {Set<{ suspend?: () => void, resume?: () => void, play?: () => void }>} */
const motionToResume = new Set();

/**
 * @param {ParentNode | null} root
 * @param {(swiper: any) => void} callback
 */
function forEachSwiperIn(root, callback) {
  if (!root) return;
  const nodes = root.getElementsByClassName('swiper');
  for (let i = 0; i < nodes.length; i++) {
    const swiper = /** @type {any} */ (nodes[i]).swiper;
    if (swiper) callback(swiper);
  }
}

function pauseBackgroundMotion() {
  swipersToResume.clear();
  motionToResume.clear();

  forEachSwiperIn(document.getElementById('MainContent'), (swiper) => {
    if (swiper.autoplay?.running) {
      swipersToResume.add(swiper);
      swiper.autoplay.stop();
    }
  });

  const header = document.getElementById('header-group');
  if (!(header instanceof HTMLElement)) return;

  header.querySelectorAll('slideshow-component, announcement-bar-component').forEach((el) => {
    const motion = /** @type {any} */ (el);
    if (typeof motion.suspend === 'function') {
      motion.suspend();
      motionToResume.add(motion);
    }
  });
}

function resumeBackgroundMotion() {
  for (const swiper of swipersToResume) {
    swiper?.autoplay?.start?.();
  }
  swipersToResume.clear();

  for (const motion of motionToResume) {
    motion.resume?.() ?? motion.play?.();
  }
  motionToResume.clear();
}

function restoreBodyScroll(scrollY) {
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: scrollY, behavior: 'instant' });
}

/**
 * Run after the open interaction has painted. No timeout — wait for a real idle
 * so this cannot join search-open INP or hitch the first keystroke.
 * @param {() => void} fn
 */
function whenBrowserIdle(fn) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fn);
    return;
  }
  setTimeout(fn, 300);
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** Invalidates in-flight search-overlay freeze if close happens first. */
  #searchOverlaySession = 0;

  /** @type {boolean} */
  #searchClosePending = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  get #isSearchModal() {
    return this.id === SEARCH_MODAL_ID;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const searchSession = this.#isSearchModal ? ++this.#searchOverlaySession : 0;
    if (this.#isSearchModal) this.#searchClosePending = false;

    // Prevent layout thrashing by separating DOM reads from DOM writes.
    // Search open INP depends on this staying off the click path (same as before).
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      setTimeout(() => {
        dialog.showModal();
        if (searchSession) {
          whenBrowserIdle(() => {
            if (searchSession !== this.#searchOverlaySession || !dialog.open) return;
            pauseBackgroundMotion();
            freezePageBehindSearchModal();
          });
        }
      }, 0);
      requestIdleCallback(() => this.dispatchEvent(new DialogOpenEvent()));

      setTimeout(() => {
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      }, 0);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (this.#isSearchModal) {
      this.#closeSearchModal(dialog);
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    restoreBodyScroll(this.#previousScrollY);

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Commit the close animation on the click path; restore the page after paint.
   * @param {HTMLDialogElement} dialog
   */
  #closeSearchModal(dialog) {
    if (this.#searchClosePending) return;
    this.#searchClosePending = true;
    this.#searchOverlaySession += 1;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    const finish = () => {
      if (!this.#searchClosePending) return;
      this.#searchClosePending = false;

      requestYieldCallback(() => {
        unfreezePageBehindSearchModal();
        restoreBodyScroll(this.#previousScrollY);
        dialog.close();
        dialog.classList.remove('dialog-closing');
        resumeBackgroundMotion();

        this.dispatchEvent(new DialogCloseEvent());
      });
    };

    const onAnimationEndEvent = (/** @type {AnimationEvent} */ event) => {
      if (event.target !== dialog) return;
      dialog.removeEventListener('animationend', onAnimationEndEvent);
      finish();
    };

    dialog.addEventListener('animationend', onAnimationEndEvent);
    setTimeout(finish, SEARCH_MODAL_CLOSE_MS + 50);
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
